import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, QrCode, Palette, Zap, Globe, Smartphone, Apple, ChevronRight, Check, MousePointerClick, Users, Wand2, Hammer } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import MobileFrame from "../components/MobileFrame";

export default function Landing() {
  const { t, isRTL } = useI18n();

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="aurora" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-20 sm:pt-20 sm:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 z-10">
              <span className="chip rise rise-1" data-testid="hero-eyebrow">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                {t("hero_eyebrow")}
              </span>
              <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.35] sm:leading-[1.3] tracking-tight rise rise-2">
                <span className="block text-zinc-900 mb-2">{t("hero_title_1")}</span>
                <span className="block text-gradient pb-2 mb-2">{t("hero_title_2")}</span>
                <span className="block text-zinc-900">{t("hero_title_3")}</span>
              </h1>
              <p className="mt-7 max-w-xl text-lg text-zinc-600 leading-relaxed rise rise-3">
                {t("hero_sub")}
              </p>

              <div className="mt-9 flex flex-wrap gap-3 rise rise-4">
                <Link to="/quick" data-testid="hero-cta-quick" className="btn btn-primary btn-lg">
                  {t("home_quick_card_title")}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} strokeWidth={2} />
                </Link>
                <Link to="/register" data-testid="hero-cta-build" className="btn btn-secondary btn-lg">
                  {t("home_build_card_title")}
                </Link>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500">
                {[t("hero_pill_1"), t("hero_pill_2"), t("hero_pill_3")].map((p) => (
                  <span key={p} className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2.5} />
                    {p}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 z-10">
              <div className="relative max-w-[320px] mx-auto">
                <div
                  className="absolute -inset-12 -z-10 rounded-full opacity-60"
                  style={{
                    background: "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)",
                  }}
                />
                <MobileFrame
                  appName="Acme"
                  shortName="Acme"
                  themeColor="#4F46E5"
                  backgroundColor="#FAFAF7"
                  url="acme.com"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quiet trust strip */}
        <div className="border-y border-zinc-200/80 py-4 overflow-hidden bg-white/40">
          <div className="marquee text-xs font-medium uppercase tracking-[0.18em] text-zinc-500">
            {Array.from({ length: 2 }).map((_, idx) => (
              <span key={idx} className="flex items-center gap-10 mx-5">
                {["Paste a link", "Pick an icon", "Share a QR", "Install on home screen", "No App Store fees", "Bilingual ready"].map((txt) => (
                  <span key={txt} className="flex items-center gap-10">
                    <span>{txt}</span>
                    <ChevronRight className="w-3 h-3 opacity-40" strokeWidth={2} />
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* MODE PICKER — Quick App vs Build App */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-xl mx-auto mb-10">
            <span className="chip">{t("home_pick_mode")}</span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-bold tracking-tight">
              {t("home_pick_mode")}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <Link
              to="/quick"
              data-testid="home-mode-quick"
              className="surface-card surface-card-hover p-7 sm:p-9 flex flex-col gap-4 group"
            >
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl bg-zinc-900 grid place-items-center">
                  <Wand2 className="w-5 h-5 text-white" strokeWidth={1.8} />
                </span>
                <span className="chip ms-auto">{t("home_quick_chip")}</span>
              </div>
              <h3 className="font-display text-2xl font-semibold tracking-tight mt-1">
                {t("home_quick_card_title")}
              </h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                {t("home_quick_card_desc")}
              </p>
              <div className="mt-auto pt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-900">
                {t("home_quick_card_cta")}
                <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isRTL ? "rotate-180 group-hover:-translate-x-1" : ""}`} strokeWidth={2} />
              </div>
            </Link>

            <Link
              to="/register"
              data-testid="home-mode-build"
              className="surface-card surface-card-hover p-7 sm:p-9 flex flex-col gap-4 group"
              style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FAFAF7 100%)" }}
            >
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl bg-zinc-900 grid place-items-center">
                  <Hammer className="w-5 h-5 text-white" strokeWidth={1.8} />
                </span>
                <span className="chip ms-auto !bg-zinc-900 !text-white !border-zinc-900">{t("home_build_chip")}</span>
              </div>
              <h3 className="font-display text-2xl font-semibold tracking-tight mt-1">
                {t("home_build_card_title")}
              </h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                {t("home_build_card_desc")}
              </p>
              <div className="mt-auto pt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-900">
                {t("home_build_card_cta")}
                <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isRTL ? "rotate-180 group-hover:-translate-x-1" : ""}`} strokeWidth={2} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <span className="chip" data-testid="features-eyebrow">Why teams pick us</span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
              {t("features_title")}
            </h2>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Globe, title: t("features_1_title"), desc: t("features_1_desc") },
              { icon: Palette, title: t("features_2_title"), desc: t("features_2_desc") },
              { icon: QrCode, title: t("features_3_title"), desc: t("features_3_desc") },
              { icon: Zap, title: t("features_4_title"), desc: t("features_4_desc") },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className="surface-card surface-card-hover p-6 flex flex-col gap-4"
                  data-testid={`feature-card-${i}`}
                >
                  <span className="w-10 h-10 rounded-xl bg-zinc-900 grid place-items-center">
                    <Icon className="w-5 h-5 text-white" strokeWidth={1.8} />
                  </span>
                  <h3 className="font-display font-semibold text-lg tracking-tight">{f.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <span className="chip">3 steps</span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-bold tracking-tight">
              {t("how_title")}
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {[t("how_1"), t("how_2"), t("how_3")].map((step, i) => (
              <div key={i} className="surface-card p-7" data-testid={`how-step-${i}`}>
                <span className="font-mono text-xs text-zinc-400 tracking-wider">0{i + 1}</span>
                <h3 className="font-display font-semibold text-xl tracking-tight mt-3">{step}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR OWNERS — second value prop */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="surface-card overflow-hidden p-8 sm:p-12 lg:p-16 relative" data-testid="owners-section">
            <div
              className="absolute -top-20 -end-20 w-[26rem] h-[26rem] rounded-full opacity-60 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(99,102,241,0.20), transparent 70%)" }}
            />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 z-10">
                <span className="chip" data-testid="owners-eyebrow">
                  <Users className="w-3.5 h-3.5" strokeWidth={2} />
                  {t("owners_eyebrow")}
                </span>
                <h2 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.2]">
                  {t("owners_title")}
                </h2>
                <p className="mt-5 max-w-xl text-lg text-zinc-600 leading-relaxed">
                  {t("owners_sub")}
                </p>

                <ul className="mt-7 space-y-2.5">
                  {[t("owners_b1"), t("owners_b2"), t("owners_b3")].map((b, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm font-medium text-zinc-700">
                      <span className="w-5 h-5 rounded-full bg-zinc-900 text-white grid place-items-center flex-shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/register"
                  data-testid="owners-cta"
                  className="btn btn-primary btn-lg mt-8 inline-flex"
                >
                  <MousePointerClick className="w-4 h-4" strokeWidth={2} />
                  {t("owners_cta")}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} strokeWidth={2} />
                </Link>
              </div>

              <div className="lg:col-span-5 z-10">
                <div className="surface-card p-5 max-w-sm mx-auto" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FAFAF7 100%)" }}>
                  <div className="text-[10px] font-mono text-zinc-400 mb-3">your-site.com</div>
                  <div className="bg-zinc-50 rounded-2xl p-5 border border-zinc-200/80 relative overflow-hidden">
                    <div className="h-2.5 w-24 bg-zinc-200 rounded-full mb-3" />
                    <div className="h-2 w-32 bg-zinc-200 rounded-full mb-2" />
                    <div className="h-2 w-20 bg-zinc-200 rounded-full mb-5" />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm shadow-md"
                      onClick={(e) => e.preventDefault()}
                      tabIndex={-1}
                    >
                      <Smartphone className="w-3.5 h-3.5" strokeWidth={2} />
                      Install as app
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-3 text-center">
                    👆 One-tap install button on your site
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORMS BAND */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="surface-card p-10 sm:p-14 relative overflow-hidden">
            <div
              className="absolute -inset-1 -z-0 opacity-50"
              style={{
                background:
                  "radial-gradient(circle at 80% 0%, rgba(99,102,241,0.18), transparent 50%), radial-gradient(circle at 0% 100%, rgba(6,182,212,0.18), transparent 50%)",
              }}
            />
            <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="chip">{t("hero_pill_2")}</span>
                <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-4 text-zinc-900">
                  {t("install_title")}
                </h3>
                <p className="text-zinc-600 mt-3 max-w-sm">
                  {t("install_sub")}
                </p>
                <div className="flex gap-6 mt-6">
                  <span className="flex items-center gap-2 text-sm font-medium text-zinc-700">
                    <Apple className="w-5 h-5" strokeWidth={1.6} />
                    iOS · Safari
                  </span>
                  <span className="flex items-center gap-2 text-sm font-medium text-zinc-700">
                    <Smartphone className="w-5 h-5" strokeWidth={1.6} />
                    Android · Chrome
                  </span>
                </div>
              </div>
              <div className="flex justify-center md:justify-end">
                <div className="max-w-[260px] w-full">
                  <MobileFrame
                    appName="Demo"
                    shortName="Demo"
                    themeColor="#06B6D4"
                    backgroundColor="#FFFFFF"
                    url="example.com"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tight">
            {t("hero_cta_primary")}
          </h2>
          <p className="text-zinc-600 mt-5 max-w-xl mx-auto">{t("hero_sub")}</p>
          <Link
            to="/register"
            data-testid="how-cta"
            className="btn btn-primary btn-lg mt-8 inline-flex"
          >
            {t("plan_cta_free")}
            <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} strokeWidth={2} />
          </Link>
        </div>
      </section>
    </div>
  );
}
